import { Calendar, TrendingUp, History as HistoryIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useGymCheckins } from '@/hooks/useGymCheckins';
import { useExercises } from '@/context/ExerciseContext';
import { useState } from 'react';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { getUserLocalDate } from '@/utils/timezone';

export default function History() {
  const { checkins } = useGymCheckins();
  const { getExercisesByDate } = useExercises();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  const selectedDateExercises = selectedDate 
    ? getExercisesByDate(getUserLocalDate(selectedDate))
    : [];

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="text-center"
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          <HistoryIcon className="w-6 h-6 text-blue-500" />
          <h1 className="text-2xl font-bold">Workout History</h1>
        </div>
        <p className="text-muted-foreground">
          Track your gym visits and explore your workout history
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Enhanced Calendar */}
        <motion.div
          className="bg-card border border-border rounded-lg p-6"
          whileHover={{ y: -2 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="w-5 h-5 text-blue-500" />
            <h2 className="text-xl font-semibold">Calendar View</h2>
            {selectedDate && (
              <span className="text-sm text-muted-foreground ml-auto">
                {format(selectedDate, 'MMM d, yyyy')}
              </span>
            )}
          </div>
          
          <CalendarComponent
            mode="single"
            weekStartsOn={1}
            selected={selectedDate}
            onSelect={setSelectedDate}
            className="w-full [--cell-size:2.5rem] sm:[--cell-size:3rem]"
            modifiers={{
              checkinDay: (date) => {
                const dateString = getUserLocalDate(date);
                return checkins.some(checkin => checkin.date === dateString);
              }
            }}
            modifiersClassNames={{
              checkinDay: "bg-green-500 text-white hover:bg-green-600 data-[selected=true]:bg-green-600"
            }}
          />
          
          <div className="mt-4 flex gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>Gym check-in</span>
            </div>
          </div>
        </motion.div>

        {/* Selected Date Details */}
        <motion.div
          className="bg-card border border-border rounded-lg p-6"
          whileHover={{ y: -2 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5 text-green-500" />
            <h2 className="text-xl font-semibold">
              {selectedDate ? `Workout Details` : 'Select a Date'}
            </h2>
          </div>

          {selectedDate ? (
            <>
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">
                  {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                </h3>
                {checkins.some(checkin => checkin.date === getUserLocalDate(selectedDate)) && (
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Gym Check-in
                  </div>
                )}
              </div>

              {selectedDateExercises.length > 0 ? (
                <div className="space-y-4">
                  <h4 className="font-medium text-muted-foreground">Exercises ({selectedDateExercises.length})</h4>
                  {selectedDateExercises.map((exercise, index) => (
                    <motion.div
                      key={exercise.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="bg-muted/50 border border-border rounded-lg p-4"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="font-medium">{exercise.exercise}</h5>
                        {exercise.isPersonalRecord && (
                          <span className="text-xs bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 px-2 py-1 rounded-full font-medium">
                            PR
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <div>Weight: <span className="font-medium text-foreground">{exercise.weight}kg</span></div>
                        <div>Sets: <span className="font-medium text-foreground">{exercise.sets}</span> × <span className="font-medium text-foreground">{exercise.reps}</span> reps</div>
                        {exercise.notes && (
                          <div>Notes: <span className="font-medium text-foreground">{exercise.notes}</span></div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No exercises logged on this date</p>
                  <p className="text-sm">Select a different date to see workout details</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold mb-2">Select a date</h3>
              <p className="text-sm">Choose a date from the calendar to view your workout details</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}